/**
 * MusicalInstrumentsServicesDisplay - Main page for Musical Instruments category
 * Features:
 * - Intent-first UX (Buy/Learn/Repair/Rent)
 * - Hierarchical category structure (Instruments/Audio/Accessories/Services)
 * - Find a Teacher quick search
 * - Kids/Pro instrument filters
 * - Collapsible sidebar with counts
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Search, Filter, MapPin, Star, Clock, ChevronRight, ChevronDown, ChevronUp,
  Music, Guitar, Piano, Drum, Mic2, Radio, BookOpen, Wrench,
  Heart, ShoppingBag, Users, Loader2, ExternalLink, Calendar,
  Check, X, FileQuestion, GraduationCap, Package, Sparkles,
  Baby, Award, Speaker, Headphones, Volume2
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hierarchical Category Structure
const CATEGORY_HIERARCHY = {
  instruments: {
    name: 'Instruments',
    icon: Guitar,
    color: 'indigo',
    subcategories: [
      { slug: 'guitars', name: 'Guitars & Basses', icon: Guitar },
      { slug: 'keyboards-pianos', name: 'Keyboards & Pianos', icon: Piano },
      { slug: 'drums-percussion', name: 'Drums & Percussion', icon: Drum },
      { slug: 'string-instruments', name: 'String Instruments', icon: Music },
      { slug: 'wind-instruments', name: 'Wind Instruments', icon: Mic2 },
      { slug: 'traditional-instruments', name: 'Traditional Instruments', icon: Drum },
    ]
  },
  audioGear: {
    name: 'Audio & Gear',
    icon: Speaker,
    color: 'blue',
    subcategories: [
      { slug: 'dj-equipment', name: 'DJ & Studio Equipment', icon: Radio },
      { slug: 'amplifiers-effects', name: 'Amps & Effects', icon: Volume2 },
      { slug: 'audio-equipment', name: 'Audio Equipment', icon: Speaker },
      { slug: 'recording-gear', name: 'Recording Gear', icon: Headphones },
    ]
  },
  accessories: {
    name: 'Accessories',
    icon: Wrench,
    color: 'amber',
    subcategories: [
      { slug: 'music-accessories', name: 'Accessories & Parts', icon: Wrench },
      { slug: 'sheet-music', name: 'Sheet Music & Books', icon: BookOpen },
    ]
  },
  services: {
    name: 'Music Services',
    icon: Users,
    color: 'green',
    subcategories: [
      { slug: 'lessons-tuition', name: 'Lessons & Tuition', icon: GraduationCap },
      { slug: 'repairs-servicing', name: 'Repairs & Servicing', icon: Wrench },
      { slug: 'rentals-hire', name: 'Rentals & Hire', icon: Package },
    ]
  }
};

// Instruments for teacher search
const TEACHABLE_INSTRUMENTS = [
  { value: 'piano', label: 'Piano', icon: Piano },
  { value: 'guitar', label: 'Guitar', icon: Guitar },
  { value: 'drums', label: 'Drums', icon: Drum },
  { value: 'violin', label: 'Violin', icon: Music },
  { value: 'vocals', label: 'Vocals', icon: Mic2 },
  { value: 'bass', label: 'Bass Guitar', icon: Guitar },
  { value: 'keyboard', label: 'Keyboard', icon: Piano },
  { value: 'flute', label: 'Flute', icon: Mic2 },
  { value: 'saxophone', label: 'Saxophone', icon: Mic2 },
  { value: 'traditional', label: 'Traditional', icon: Drum },
];

// Intent Options
const INTENT_OPTIONS = [
  { id: 'buy', label: 'Buy / Sell', icon: ShoppingBag, color: 'indigo', description: 'Browse instruments & equipment', targetGroup: 'instruments' },
  { id: 'learn', label: 'Learn', icon: GraduationCap, color: 'green', description: 'Find music teachers & lessons', targetSlug: 'lessons-tuition' },
  { id: 'repair', label: 'Repair', icon: Wrench, color: 'orange', description: 'Get repairs & servicing', targetSlug: 'repairs-servicing' },
  { id: 'rent', label: 'Rent', icon: Package, color: 'cyan', description: 'Rent instruments & equipment', targetSlug: 'rentals-hire' },
];

// Collapsible Category Group Component
function CategoryGroup({ group, groupKey, counts, selectedCategory, onCategorySelect, expanded, onToggle }) {
  const Icon = group.icon;
  const colorClasses = {
    indigo: 'text-indigo-600 bg-indigo-100',
    blue: 'text-blue-600 bg-blue-100',
    amber: 'text-amber-600 bg-amber-100',
    green: 'text-green-600 bg-green-100',
  };
  
  const totalCount = group.subcategories.reduce((sum, sub) => sum + (counts[sub.slug] || 0), 0);
  
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button 
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${colorClasses[group.color]}`}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="font-medium text-gray-800">{group.name}</span>
          <Badge variant="secondary" className="text-xs">{totalCount}</Badge>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      
      {expanded && (
        <div className="pb-2 pl-4">
          {group.subcategories.map(sub => {
            const SubIcon = sub.icon;
            const count = counts[sub.slug] || 0;
            const isSelected = selectedCategory === sub.slug;
            
            return (
              <button
                key={sub.slug}
                onClick={() => onCategorySelect(sub.slug)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                  isSelected 
                    ? 'bg-indigo-50 text-indigo-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <SubIcon className="w-4 h-4" />
                  <span>{sub.name}</span>
                </div>
                <span className={isSelected ? 'text-indigo-600' : 'text-gray-400'}>{count}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Find Teacher Quick Search Component
function FindTeacherSearch({ onSearch, country }) {
  const [instrument, setInstrument] = useState('');
  const [location, setLocation] = useState('');
  const [level, setLevel] = useState('');
  
  const cities = country === 'UK' 
    ? ['London', 'Manchester', 'Birmingham', 'Leeds', 'Bristol']
    : ['Colombo', 'Kandy', 'Galle', 'Jaffna', 'Negombo'];
  
  return (
    <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 mb-6">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <GraduationCap className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-bold text-green-900">Find a Music Teacher</h3>
            <p className="text-sm text-green-700">Search by instrument, location, and skill level</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Select value={instrument} onValueChange={setInstrument}>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Select Instrument" />
            </SelectTrigger>
            <SelectContent>
              {TEACHABLE_INSTRUMENTS.map(inst => (
                <SelectItem key={inst.value} value={inst.value}>
                  <div className="flex items-center gap-2">
                    <inst.icon className="w-4 h-4" />
                    {inst.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger className="bg-white">
              <MapPin className="w-4 h-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="online">Online Only</SelectItem>
              <SelectItem value="any">Any Location</SelectItem>
              {cities.map(city => (
                <SelectItem key={city} value={city}>{city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="Skill Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any Level</SelectItem>
              <SelectItem value="beginner">Beginner</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            className="bg-green-600 hover:bg-green-700"
            onClick={() => onSearch({ instrument, location, level })}
            data-testid="find-teacher-btn"
          >
            <Search className="w-4 h-4 mr-2" />
            Find Teachers
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Intent Selector Modal
function IntentSelectorModal({ open, onClose, onSelectIntent }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">What would you like to do?</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-4 mt-4">
          {INTENT_OPTIONS.map(intent => {
            const Icon = intent.icon;
            const colorClasses = {
              indigo: 'border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50',
              green: 'border-green-200 hover:border-green-400 hover:bg-green-50',
              orange: 'border-orange-200 hover:border-orange-400 hover:bg-orange-50',
              cyan: 'border-cyan-200 hover:border-cyan-400 hover:bg-cyan-50',
            };
            const iconClasses = {
              indigo: 'bg-indigo-100 text-indigo-600',
              green: 'bg-green-100 text-green-600',
              orange: 'bg-orange-100 text-orange-600',
              cyan: 'bg-cyan-100 text-cyan-600',
            };
            
            return (
              <button
                key={intent.id}
                onClick={() => onSelectIntent(intent)}
                className={`p-4 rounded-xl border-2 transition-all ${colorClasses[intent.color]}`}
                data-testid={`intent-${intent.id}`}
              >
                <div className={`w-12 h-12 rounded-lg ${iconClasses[intent.color]} flex items-center justify-center mx-auto mb-3`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-gray-800">{intent.label}</h3>
                <p className="text-sm text-gray-500 mt-1">{intent.description}</p>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Kids/Pro Filter Component
function SkillLevelFilters({ filters, onChange }) {
  return (
    <div className="flex gap-2 flex-wrap">
      <Button
        variant={filters.kidsOnly ? "default" : "outline"}
        size="sm"
        onClick={() => onChange({ ...filters, kidsOnly: !filters.kidsOnly })}
        className={filters.kidsOnly ? "bg-pink-500 hover:bg-pink-600" : ""}
        data-testid="kids-filter-btn"
      >
        <Baby className="w-4 h-4 mr-1" />
        Kids Instruments
      </Button>
      <Button
        variant={filters.proOnly ? "default" : "outline"}
        size="sm"
        onClick={() => onChange({ ...filters, proOnly: !filters.proOnly })}
        className={filters.proOnly ? "bg-purple-600 hover:bg-purple-700" : ""}
        data-testid="pro-filter-btn"
      >
        <Award className="w-4 h-4 mr-1" />
        Pro Instruments
      </Button>
    </div>
  );
}

// Provider Card Component
function ProviderCard({ provider, currencySymbol, onBook }) {
  const navigate = useNavigate();
  
  return (
    <Card className="group hover:shadow-lg transition-all overflow-hidden" data-testid={`provider-${provider.provider_id || provider.id}`}>
      <div className={`h-20 bg-gradient-to-r ${
        provider.provider_type === 'TEACHER' || provider.provider_type === 'music_teacher' ? 'from-green-500 to-emerald-500' :
        provider.provider_type === 'REPAIR_TECH' || provider.provider_type === 'repair_technician' ? 'from-orange-500 to-red-500' :
        provider.provider_type === 'RENTAL_SHOP' || provider.provider_type === 'rental_shop' ? 'from-cyan-500 to-blue-500' :
        provider.provider_type === 'piano_tuner' ? 'from-blue-500 to-cyan-500' :
        'from-indigo-500 to-purple-500'
      } relative`}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '15px 15px'
          }} />
        </div>
        {(provider.verification_level >= 2 || provider.is_sponsored) && (
          <Badge className="absolute top-2 right-2 bg-white/20 text-white border-white/30">
            <Check className="w-3 h-3 mr-1" /> Verified
          </Badge>
        )}
      </div>
      
      <CardContent className="pt-4">
        <h3 className="font-bold text-lg">{provider.display_name || provider.provider_name}</h3>
        <p className="text-sm text-gray-500 capitalize">
          {provider.provider_type?.replace(/_/g, ' ')}
        </p>
        
        <p className="text-sm text-gray-600 mt-2 line-clamp-2">
          {provider.description}
        </p>
        
        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            {provider.rating?.toFixed(1) || 'New'}
            {(provider.reviews_count || provider.review_count) > 0 && (
              <span className="text-gray-400">({provider.reviews_count || provider.review_count})</span>
            )}
          </span>
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {provider.city || provider.location?.split(',')[0]}
          </span>
        </div>
        
        <div className="flex flex-wrap gap-1 mt-3">
          {provider.online_lessons && (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
              Online Available
            </Badge>
          )}
          {provider.home_visits && (
            <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
              Home Visits
            </Badge>
          )}
          {(provider.experience_years || provider.years_in_business) && (
            <Badge variant="secondary" className="text-xs">
              {provider.experience_years || provider.years_in_business}+ years
            </Badge>
          )}
        </div>
        
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <div>
            <span className="text-gray-500 text-sm">From</span>
            <p className="font-bold text-lg text-indigo-600">
              {currencySymbol}{provider.hourly_rate || provider.starting_price}/hr
            </p>
          </div>
          <Button 
            className="bg-gradient-to-r from-indigo-500 to-purple-600"
            onClick={() => onBook(provider)}
          >
            Book Now
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Listing Card Component
function ListingCard({ listing, currencySymbol }) {
  const navigate = useNavigate();

  const openDetail = () => {
    const id = listing.listing_id || listing.id;
    if (id) navigate(`/listing/${id}`);
  };

  const conditionColors = {
    new: 'bg-green-100 text-green-700',
    'Brand New': 'bg-green-100 text-green-700',
    like_new: 'bg-blue-100 text-blue-700',
    'Like New': 'bg-blue-100 text-blue-700',
    good: 'bg-yellow-100 text-yellow-700',
    'Good': 'bg-yellow-100 text-yellow-700',
    fair: 'bg-orange-100 text-orange-700',
    'Used': 'bg-orange-100 text-orange-700',
    for_parts: 'bg-red-100 text-red-700',
  };
  
  return (
    <Card
      className="group hover:shadow-lg transition-all overflow-hidden cursor-pointer"
      onClick={openDetail}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(); } }}
      role="button"
      tabIndex={0}
      data-testid={`listing-${listing.listing_id || listing.id}`}
    >
      <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
        {(listing.media_urls?.[0] || listing.images?.[0]) ? (
          <img 
            src={listing.media_urls?.[0] || listing.images?.[0]} 
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music className="w-16 h-16 text-gray-300" />
          </div>
        )}
        {(listing.moderation?.high_value || listing.featured) && (
          <Badge className="absolute top-2 left-2 bg-amber-500">
            <Star className="w-3 h-3 mr-1" /> Featured
          </Badge>
        )}
        {listing.skill_level === 'kids' && (
          <Badge className="absolute top-2 left-2 bg-pink-500">
            <Baby className="w-3 h-3 mr-1" /> Kids
          </Badge>
        )}
        {listing.skill_level === 'pro' && (
          <Badge className="absolute top-2 left-2 bg-purple-600">
            <Award className="w-3 h-3 mr-1" /> Pro
          </Badge>
        )}
        {listing.condition && (
          <Badge className={`absolute top-2 right-2 ${conditionColors[listing.condition] || 'bg-gray-100'}`}>
            {listing.condition?.replace('_', ' ')}
          </Badge>
        )}
        <button
          className="absolute bottom-2 right-2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <Heart className="w-4 h-4 text-gray-600" />
        </button>
      </div>
      
      <CardContent className="p-4">
        <h3 className="font-semibold line-clamp-2">{listing.title}</h3>
        
        <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
          {listing.brand && <span>{listing.brand}</span>}
          {listing.brand && listing.year && <span>•</span>}
          {listing.year && <span>{listing.year}</span>}
        </div>
        
        <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
          <MapPin className="w-3 h-3" />
          <span>{listing.city || listing.location}</span>
        </div>
        
        <div className="flex items-center justify-between mt-3 pt-3 border-t">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-indigo-600">
              {currencySymbol}{listing.price?.toLocaleString()}
            </span>
            {listing.negotiable && (
              <Badge variant="outline" className="text-xs">Negotiable</Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function MusicalInstrumentsServicesDisplay() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { country } = useCountry();
  
  // State
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [listings, setListings] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [city, setCity] = useState('');
  const [currencySymbol, setCurrencySymbol] = useState(country === 'UK' ? '£' : 'Rs.');
  
  // New state for enhanced features
  const [showIntentModal, setShowIntentModal] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    instruments: true,
    audioGear: false,
    accessories: false,
    services: true,
  });
  const [skillFilters, setSkillFilters] = useState({ kidsOnly: false, proOnly: false });
  const [teacherSearch, setTeacherSearch] = useState(null);
  
  // Toggle category group expansion
  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };
  
  // Fetch categories and counts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/music/categories?country=${country}`);
        const data = await response.json();
        if (data.success) {
          setCategories(data.categories);
          setCurrencySymbol(data.currency_symbol || (country === 'UK' ? '£' : 'Rs.'));
        }
        
        // Fetch counts for all categories
        const countsResponse = await fetch(`${API_URL}/api/listings/counts?country_code=${country}`);
        if (countsResponse.ok) {
          const countsData = await countsResponse.json();
          setCategoryCounts(countsData.counts || {});
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, [country]);
  
  // Fetch providers and listings
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ country });
        if (selectedCategory) params.append('subcategory', selectedCategory);
        if (city && city !== 'all') params.append('city', city);
        if (skillFilters.kidsOnly) params.append('skill_level', 'kids');
        if (skillFilters.proOnly) params.append('skill_level', 'pro');
        if (teacherSearch?.instrument) params.append('instrument', teacherSearch.instrument);
        if (teacherSearch?.level && teacherSearch.level !== 'any') params.append('level', teacherSearch.level);
        
        // Fetch from music API
        const [providersRes, listingsRes] = await Promise.all([
          fetch(`${API_URL}/api/music/providers?${params}`).catch(() => ({ ok: false })),
          fetch(`${API_URL}/api/music/listings?${params}`).catch(() => ({ ok: false })),
        ]);
        
        let providersData = { providers: [] };
        let listingsData = { listings: [] };
        
        if (providersRes.ok) {
          providersData = await providersRes.json();
        }
        if (listingsRes.ok) {
          listingsData = await listingsRes.json();
        }
        
        // Also fetch from services API for the new service categories
        if (!selectedCategory || ['lessons-tuition', 'repairs-servicing', 'rentals-hire'].includes(selectedCategory)) {
          const serviceType = selectedCategory || '';
          const servicesRes = await fetch(`${API_URL}/api/services/providers?service_type=${serviceType}&country=${country}&limit=10`).catch(() => ({ ok: false }));
          if (servicesRes.ok) {
            const servicesData = await servicesRes.json();
            if (servicesData.providers?.length > 0) {
              providersData.providers = [...(providersData.providers || []), ...servicesData.providers];
            }
          }
        }
        
        // Also fetch from general listings API
        if (selectedCategory) {
          const generalListingsRes = await fetch(`${API_URL}/api/listings/?category=${selectedCategory}&country_code=${country}`).catch(() => ({ ok: false }));
          if (generalListingsRes.ok) {
            const generalListingsData = await generalListingsRes.json();
            if (generalListingsData.listings?.length > 0) {
              listingsData.listings = [...(listingsData.listings || []), ...generalListingsData.listings];
            }
          }
        }
        
        setProviders(providersData.providers || []);
        setListings(listingsData.listings || []);
        
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [country, selectedCategory, city, skillFilters, teacherSearch]);
  
  // Handle category selection
  const handleCategorySelect = (slug) => {
    if (selectedCategory === slug) {
      setSelectedCategory(null);
      setSearchParams({});
    } else {
      setSelectedCategory(slug);
      setSearchParams({ category: slug });
    }
  };
  
  // Handle intent selection
  const handleIntentSelect = (intent) => {
    setShowIntentModal(false);
    if (intent.targetSlug) {
      handleCategorySelect(intent.targetSlug);
      // Expand the services group
      setExpandedGroups(prev => ({ ...prev, services: true }));
    } else if (intent.targetGroup) {
      setExpandedGroups(prev => ({ ...prev, [intent.targetGroup]: true }));
    }
  };
  
  // Handle teacher search
  const handleTeacherSearch = (searchParams) => {
    setTeacherSearch(searchParams);
    handleCategorySelect('lessons-tuition');
  };
  
  // Handle booking
  const handleBookProvider = (provider) => {
    navigate(`/music/book/${provider.provider_id || provider.id}`);
  };
  
  // Filter data based on search
  const filteredProviders = useMemo(() => {
    if (!searchQuery) return providers;
    const query = searchQuery.toLowerCase();
    return providers.filter(p => 
      (p.display_name || p.provider_name || '').toLowerCase().includes(query) ||
      (p.description || '').toLowerCase().includes(query) ||
      (p.specialties || p.specializations || []).some(s => s.toLowerCase().includes(query))
    );
  }, [providers, searchQuery]);
  
  const filteredListings = useMemo(() => {
    if (!searchQuery) return listings;
    const query = searchQuery.toLowerCase();
    return listings.filter(l => 
      (l.title || '').toLowerCase().includes(query) ||
      (l.description || '').toLowerCase().includes(query) ||
      (l.brand || '').toLowerCase().includes(query)
    );
  }, [listings, searchQuery]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Music className="w-10 h-10" />
              <div>
                <h1 className="text-3xl font-bold">Musical Instruments</h1>
                <p className="text-indigo-200">
                  Buy, sell, rent instruments and book music services
                </p>
              </div>
            </div>
            
            {/* Intent Selector Button */}
            <Button 
              onClick={() => setShowIntentModal(true)}
              className="bg-white/20 hover:bg-white/30 border border-white/30"
              data-testid="what-to-do-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              What would you like to do?
            </Button>
          </div>
          
          {/* Search Bar */}
          <div className="flex gap-3 mt-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input 
                placeholder="Search instruments, brands, services..."
                className="pl-10 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/60"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="w-40 h-12 bg-white/10 border-white/20 text-white">
                <MapPin className="w-4 h-4 mr-2" />
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {country === 'UK' ? (
                  <>
                    <SelectItem value="London">London</SelectItem>
                    <SelectItem value="Manchester">Manchester</SelectItem>
                    <SelectItem value="Birmingham">Birmingham</SelectItem>
                    <SelectItem value="Leeds">Leeds</SelectItem>
                    <SelectItem value="Bristol">Bristol</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="Colombo">Colombo</SelectItem>
                    <SelectItem value="Kandy">Kandy</SelectItem>
                    <SelectItem value="Galle">Galle</SelectItem>
                    <SelectItem value="Jaffna">Jaffna</SelectItem>
                    <SelectItem value="Negombo">Negombo</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Find a Teacher Quick Search */}
        <FindTeacherSearch onSearch={handleTeacherSearch} country={country} />
        
        {/* Intent Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {INTENT_OPTIONS.map(intent => {
            const Icon = intent.icon;
            const bgColors = {
              indigo: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100',
              green: 'bg-green-50 border-green-200 hover:bg-green-100',
              orange: 'bg-orange-50 border-orange-200 hover:bg-orange-100',
              cyan: 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100',
            };
            const iconColors = {
              indigo: 'text-indigo-600',
              green: 'text-green-600',
              orange: 'text-orange-600',
              cyan: 'text-cyan-600',
            };
            
            return (
              <button
                key={intent.id}
                onClick={() => handleIntentSelect(intent)}
                className={`p-4 rounded-xl border transition-all ${bgColors[intent.color]}`}
                data-testid={`quick-${intent.id}`}
              >
                <Icon className={`w-6 h-6 ${iconColors[intent.color]} mx-auto mb-2`} />
                <p className="font-medium text-gray-800">{intent.label}</p>
              </button>
            );
          })}
        </div>
        
        <div className="flex gap-6">
          {/* Sidebar with Hierarchical Categories */}
          <div className="w-64 flex-shrink-0 hidden lg:block">
            <Card className="sticky top-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Categories
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {/* Clear Selection */}
                <button
                  onClick={() => handleCategorySelect(null)}
                  className={`w-full flex items-center justify-between px-4 py-3 border-b transition-colors ${
                    !selectedCategory ? 'bg-indigo-50 text-indigo-700 font-medium' : 'hover:bg-gray-50'
                  }`}
                >
                  <span>All Categories</span>
                  <Badge variant="secondary">{Object.values(categoryCounts).reduce((a, b) => a + b, 0)}</Badge>
                </button>
                
                {/* Category Groups */}
                {Object.entries(CATEGORY_HIERARCHY).map(([key, group]) => (
                  <CategoryGroup
                    key={key}
                    group={group}
                    groupKey={key}
                    counts={categoryCounts}
                    selectedCategory={selectedCategory}
                    onCategorySelect={handleCategorySelect}
                    expanded={expandedGroups[key]}
                    onToggle={() => toggleGroup(key)}
                  />
                ))}
              </CardContent>
            </Card>
            
            {/* Kids/Pro Filters */}
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Skill Level</CardTitle>
              </CardHeader>
              <CardContent>
                <SkillLevelFilters filters={skillFilters} onChange={setSkillFilters} />
              </CardContent>
            </Card>
          </div>
          
          {/* Main Content */}
          <div className="flex-1">
            {/* Active Filters */}
            {(selectedCategory || skillFilters.kidsOnly || skillFilters.proOnly) && (
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-sm text-gray-500">Active filters:</span>
                {selectedCategory && (
                  <Badge 
                    variant="secondary" 
                    className="cursor-pointer"
                    onClick={() => handleCategorySelect(null)}
                  >
                    {selectedCategory.replace(/-/g, ' ')}
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                )}
                {skillFilters.kidsOnly && (
                  <Badge 
                    className="bg-pink-100 text-pink-700 cursor-pointer"
                    onClick={() => setSkillFilters(f => ({ ...f, kidsOnly: false }))}
                  >
                    <Baby className="w-3 h-3 mr-1" /> Kids
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                )}
                {skillFilters.proOnly && (
                  <Badge 
                    className="bg-purple-100 text-purple-700 cursor-pointer"
                    onClick={() => setSkillFilters(f => ({ ...f, proOnly: false }))}
                  >
                    <Award className="w-3 h-3 mr-1" /> Pro
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                )}
              </div>
            )}
            
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList className="bg-white shadow-sm">
                <TabsTrigger value="all" className="flex items-center gap-2">
                  <Music className="w-4 h-4" />
                  All
                </TabsTrigger>
                <TabsTrigger value="services" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Services
                </TabsTrigger>
                <TabsTrigger value="listings" className="flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4" />
                  Buy & Sell
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <>
                {/* Music Services Section */}
                {(activeTab === 'all' || activeTab === 'services') && filteredProviders.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-indigo-600" />
                        <h2 className="text-xl font-bold">Music Services</h2>
                        <Badge variant="secondary">{filteredProviders.length}</Badge>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => navigate('/category/lessons-tuition')}>
                        View All
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredProviders.slice(0, 6).map(provider => (
                        <ProviderCard 
                          key={provider.provider_id || provider.id}
                          provider={provider}
                          currencySymbol={currencySymbol}
                          onBook={handleBookProvider}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Listings Section */}
                {(activeTab === 'all' || activeTab === 'listings') && filteredListings.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5 text-indigo-600" />
                        <h2 className="text-xl font-bold">Instruments & Equipment</h2>
                        <Badge variant="secondary">{filteredListings.length}</Badge>
                      </div>
                      <Button variant="outline" size="sm">
                        View All
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {filteredListings.map(listing => (
                        <ListingCard 
                          key={listing.listing_id || listing.id}
                          listing={listing}
                          currencySymbol={currencySymbol}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Empty State */}
                {filteredProviders.length === 0 && filteredListings.length === 0 && (
                  <Card className="p-12 text-center">
                    <Music className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No results found</h3>
                    <p className="text-gray-500 mb-4">
                      Try adjusting your filters or search query
                    </p>
                    <Button onClick={() => {
                      setSelectedCategory(null);
                      setSearchQuery('');
                      setSkillFilters({ kidsOnly: false, proOnly: false });
                    }}>
                      Clear All Filters
                    </Button>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Intent Selector Modal */}
      <IntentSelectorModal 
        open={showIntentModal}
        onClose={() => setShowIntentModal(false)}
        onSelectIntent={handleIntentSelect}
      />
    </div>
  );
}
